import {
  AccentIcon,
  AccentVariantEnum,
} from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  Icon,
  IconColor,
  IconName,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import { Tooltip } from '@delmonicos/shared-design-system-react/src/components/tooltip/tooltip';
import {
  Text,
  TextColor,
  TextFontSize,
  TextWeight,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { toCurrencyString } from '@delmonicos/shared-sdk/libs/core/src/utils/formatters/currency.formatters';
import { toNumberString } from '@delmonicos/shared-sdk/libs/core/src/utils/formatters/number.formatters';
import { TransactionReportOverviewStatisticsModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction-report/transaction-reports-overview-statistics.model';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import * as css from 'src/feature/charge-point/charge-point-details/charge-point-details.module.scss';

export enum StatisticsCardVariantEnum {
  Transactions = 'transactions',
  Revenue = 'revenue',
  Energy = 'energy',
}

interface StatisticsVariation {
  name: string;
  icon: IconName;
  iconVariant: AccentVariantEnum;
  unit?: string;
  formatter: (value: number) => string;
}

const statisticsVariations: Record<
  StatisticsCardVariantEnum,
  StatisticsVariation
> = {
  [StatisticsCardVariantEnum.Transactions]: {
    name: 'transactions',
    icon: 'electric-car-2',
    iconVariant: AccentVariantEnum.Primary,
    unit: 'sessions',
    formatter: (value) => toNumberString(value, 'fr-FR', 0, 0),
  },
  [StatisticsCardVariantEnum.Energy]: {
    name: 'energy',
    icon: 'lightning-02',
    iconVariant: AccentVariantEnum.Charging,
    unit: 'kWh',
    formatter: (value) => toNumberString(value, 'fr-FR'),
  },
  [StatisticsCardVariantEnum.Revenue]: {
    name: 'revenue',
    icon: 'currency-euro-circle',
    iconVariant: AccentVariantEnum.Accent2,
    unit: 'TTC',
    formatter: (value) => toCurrencyString(value),
  },
};

interface StatisticsCardProps {
  variant?: StatisticsCardVariantEnum;
  data?: TransactionReportOverviewStatisticsModel;
}

const PercentTooltipContent = ({
  previousMonth,
  forecast,
  variation,
}: {
  previousMonth: number;
  forecast: number;
  variation: StatisticsVariation;
}): React.JSX.Element => {
  const { t } = useTranslation();

  const { formatter } = variation;

  return (
    <Stack direction="column">
      <Text>
        {t('$.charge-point-details.statistics.previous-month')}:{' '}
        {formatter(previousMonth)}
      </Text>
      <Text>
        {t('$.charge-point-details.statistics.forecast')}: {formatter(forecast)}
      </Text>
    </Stack>
  );
};

const getForcastTagStyle = (
  percent: number,
): {
  bgClass: string;
  icon: IconName;
  iconColor: IconColor;
  textColor: TextColor;
} => {
  if (percent > 0) {
    return {
      bgClass: 'bg-surface-success',
      icon: 'arrow-up',
      iconColor: IconColor.Success,
      textColor: TextColor.Success,
    };
  }
  if (percent < 0) {
    return {
      bgClass: 'bg-surface-error',
      icon: 'arrow-down',
      iconColor: IconColor.Error,
      textColor: TextColor.Error,
    };
  }
  return {
    bgClass: 'bg-surface-tertiary',
    icon: 'minus',
    iconColor: IconColor.Secondary,
    textColor: TextColor.Secondary,
  };
};

const ForecastTag = ({
  percent,
  previousMonth,
  forecast,
  variation,
}: {
  percent: number;
  previousMonth: number;
  forecast: number;
  variation: StatisticsVariation;
}): React.JSX.Element => {
  const { bgClass, icon, iconColor, textColor } = getForcastTagStyle(percent);

  return (
    <Tag className={bgClass} flex={1}>
      <Stack flex={1} alignItems="center" justifyContent="space-between">
        <Icon name={icon} color={iconColor} />
        <Stack flex={1} alignItems="center" justifyContent="center">
          <Tooltip
            content={
              <PercentTooltipContent
                previousMonth={previousMonth}
                forecast={forecast}
                variation={variation}
              />
            }>
            <Text
              className={css.percentage}
              fontSize={TextFontSize.Medium}
              italic
              color={textColor}>
              {`${percent > 0 ? '+' : percent < 0 ? '-' : ''} ${Math.abs(percent)} %`}
            </Text>
          </Tooltip>
        </Stack>
      </Stack>
    </Tag>
  );
};

export const StatisticsCard = ({
  data,
  variant = StatisticsCardVariantEnum.Transactions,
}: StatisticsCardProps): React.JSX.Element | null => {
  const { t } = useTranslation();

  if (data === undefined) {
    return null;
  }

  const { currentMonth, forecast, growthRate, previousMonth } = data;

  const variation = statisticsVariations[variant];

  return (
    <Card className={classNames([css.statisticsCard])} direction="row" flex={1}>
      <Stack flex={2} alignItems="center" gap={GapEnum.M}>
        <AccentIcon icon={variation.icon} variant={variation.iconVariant} />
        <Stack direction="column" flex={1}>
          <Text fontSize={TextFontSize.H3} fontWeight={TextWeight.Medium}>
            {variation.formatter(currentMonth)}
          </Text>
          <Text fontSize={TextFontSize.H3}>{variation.unit}</Text>
        </Stack>
      </Stack>
      <Card.FullHeight
        className={classNames([css.percentageSection, 'bg-surface-secondary'])}>
        <Stack direction="column" gap={GapEnum.XS}>
          <ForecastTag
            percent={growthRate}
            forecast={forecast}
            previousMonth={previousMonth}
            variation={variation}
          />
          <Text fontSize={TextFontSize.Small} color={TextColor.Secondary}>
            {t('$.charge-point-details.statistics.vs-previous-month')}
          </Text>
        </Stack>
      </Card.FullHeight>
    </Card>
  );
};
